import {
	MdSensors,
	MdNotificationsActive,
	MdOutlineBuild,
	MdBatteryChargingFull,
	MdWifi,
	MdSecurity,
	MdFavorite,
	MdOutlineTimer,
	MdErrorOutline,
	MdCheckCircleOutline,
	MdHome,
	MdStar,
} from "react-icons/md";

// Özellik kartlarında seçilebilecek ikonlar. Veritabanında sadece "key" saklanır.
export const FEATURE_ICONS = [
	{ key: "sensor", label: "Sensör", Icon: MdSensors },
	{ key: "alert", label: "Uyarı", Icon: MdErrorOutline },
	{ key: "heart", label: "Sağlık", Icon: MdFavorite },
	{ key: "notification", label: "Bildirim", Icon: MdNotificationsActive },
	{ key: "check", label: "Onay", Icon: MdCheckCircleOutline },
	{ key: "install", label: "Kurulum", Icon: MdOutlineBuild },
	{ key: "battery", label: "Pil", Icon: MdBatteryChargingFull },
	{ key: "wifi", label: "Bağlantı", Icon: MdWifi },
	{ key: "security", label: "Güvenlik", Icon: MdSecurity },
	{ key: "timer", label: "Zaman", Icon: MdOutlineTimer },
	{ key: "home", label: "Ev", Icon: MdHome },
	{ key: "star", label: "Yıldız", Icon: MdStar },
];

export const getFeatureIcon = (key) =>
	(FEATURE_ICONS.find((i) => i.key === key) || FEATURE_ICONS[0]).Icon;

export const formatPrice = (value, currency = "TL") => {
	const num = Number(value);
	if (value === "" || value === null || value === undefined || Number.isNaN(num)) {
		return "-";
	}
	return `${num.toLocaleString("tr-TR")} ${currency}`;
};

export const emptyFeature = () => ({ icon: "sensor", title: "", description: "" });

// Ürün/Hizmet tipi: kargoyla gönderilen fiziksel ürün veya anında teslim edilen hizmet
export const PRODUCT_TYPES = [
	{
		key: "shipping",
		label: "Kargolanacak Ürün",
		description: "Fiziksel ürün. Sipariş sonrası hazırlanır ve kargo ile gönderilir.",
	},
	{
		key: "instant",
		label: "Anlık Teslim",
		description: "Hizmet, abonelik veya dijital ürün. Ödeme sonrası anında teslim edilir.",
	},
];

export const SHIPPING_CARRIERS = [
	"Yurtiçi Kargo",
	"Aras Kargo",
	"MNG Kargo",
	"PTT Kargo",
	"Sürat Kargo",
	"UPS",
	"DHL",
	"Diğer",
];

export const emptyShipping = () => ({
	carrier: "Yurtiçi Kargo",
	feeType: "free", // free | fixed | conditional
	fee: "",
	freeAbove: "",
	preparationDays: "1",
	deliveryMinDays: "2",
	deliveryMaxDays: "5",
	weight: "",
	desi: "",
	width: "",
	height: "",
	depth: "",
	note: "",
});

export const emptyInstantDelivery = () => ({
	method: "email", // email | activation | account | other
	note: "",
});

export const INSTANT_METHODS = [
	{ key: "email", label: "E-posta ile teslim" },
	{ key: "activation", label: "Aktivasyon kodu" },
	{ key: "account", label: "Hesaba tanımlama" },
	{ key: "other", label: "Diğer" },
];

export const getDeliverySummary = (product) => {
	if (product.productType === "instant") {
		const method = INSTANT_METHODS.find((m) => m.key === product.instantDelivery?.method);
		return {
			title: "Anında Teslim",
			lines: [
				method ? method.label : "Ödeme sonrası anında teslim edilir.",
				product.instantDelivery?.note,
			].filter(Boolean),
		};
	}
	const sh = product.shipping || {};
	let feeText = "Ücretsiz Kargo";
	if (sh.feeType === "fixed") {
		feeText = `Kargo: ${formatPrice(sh.fee, product.productCurrency)}`;
	} else if (sh.feeType === "conditional") {
		feeText = `${formatPrice(sh.freeAbove, product.productCurrency)} üzeri ücretsiz kargo, altında ${formatPrice(sh.fee, product.productCurrency)}`;
	}
	const prep = Number(sh.preparationDays) || 0;
	const min = Number(sh.deliveryMinDays) || 0;
	const max = Number(sh.deliveryMaxDays) || min;
	return {
		title: "Kargo ile Teslim",
		lines: [
			sh.carrier ? `${sh.carrier} ile gönderim` : null,
			feeText,
			`Tahmini teslim: ${prep + min}-${prep + max} iş günü`,
			sh.note,
		].filter(Boolean),
	};
};

export const emptyProduct = () => ({
	productName: "",
	productSlug: "",
	productDescription: "",
	productImage: "",
	productPrice: "",
	productCurrency: "TL",
	productStock: "",
	productActive: "Aktif",
	productType: "shipping",
	shipping: emptyShipping(),
	instantDelivery: emptyInstantDelivery(),
	productFeatures: [emptyFeature()],
	productSpecs: [""],
});

// Backend'den gelen kaydı form durumuna çevirir: null sayısal alanlar
// kontrollü input'lara bağlanamayacağı için "" yapılır, eksik alt
// nesneler varsayılanlarla tamamlanır.
const nullToEmpty = (obj) =>
	Object.fromEntries(
		Object.entries(obj).map(([k, v]) => [k, v === null || v === undefined ? "" : v])
	);

export const productFromApi = (data = {}) => {
	const base = emptyProduct();
	return {
		...base,
		...nullToEmpty(data),
		productType: data.productType === "instant" ? "instant" : "shipping",
		shipping: { ...emptyShipping(), ...nullToEmpty(data.shipping || {}) },
		instantDelivery: {
			...emptyInstantDelivery(),
			...nullToEmpty(data.instantDelivery || {}),
		},
		productFeatures:
			Array.isArray(data.productFeatures) && data.productFeatures.length > 0
				? data.productFeatures.map((f) => ({ ...emptyFeature(), ...f }))
				: [emptyFeature()],
		productSpecs:
			Array.isArray(data.productSpecs) && data.productSpecs.length > 0
				? data.productSpecs
				: [""],
	};
};

export const slugify = (text) =>
	(text || "")
		.toLowerCase()
		.replace(/ğ/g, "g")
		.replace(/ü/g, "u")
		.replace(/ş/g, "s")
		.replace(/ı/g, "i")
		.replace(/ö/g, "o")
		.replace(/ç/g, "c")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "");
